import React, { useState, useEffect } from 'react';
import { X, Calendar, Users, Edit2, ExternalLink, FileText, Tag } from 'lucide-react';
import { projectBoardService } from '../../api/boardApi';

const STATUS_CONFIG = {
  todo:       { label: 'To Do',       cls: 'bg-blue-100 text-[#37559e]' },
  inProgress: { label: 'In Progress', cls: 'bg-indigo-100 text-indigo-700' },
  review:     { label: 'Review',      cls: 'bg-amber-100 text-amber-700' },
  done:       { label: 'Done',        cls: 'bg-green-100 text-green-700' },
  trash:      { label: 'Trash',       cls: 'bg-gray-100 text-gray-600' },
};

const AVATAR_PALETTE = ['#37559e','#bf6f50','#15803d','#b45309','#1d4ed8','#7c3aed','#0891b2','#be123c'];
const getInitials = (name) =>
  name ? name.trim().split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
const getAvatarColor = (name) =>
  AVATAR_PALETTE[(name?.charCodeAt(0) ?? 0) % AVATAR_PALETTE.length];

const TaskDetailsModal = ({ isOpen, onClose, task, onEdit, projectId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId || !isOpen) return;
    setLoading(true);
    projectBoardService.getProjectDocuments(projectId)
      .then(setDocuments)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId, isOpen]);

  if (!isOpen || !task) return null;

  const statusCfg = STATUS_CONFIG[task.status] || { label: task.status, cls: 'bg-gray-100 text-gray-600' };
  const assignees = Object.values(task.assignee || {}).filter(a => a?.name);

  const handleDocumentClick = (doc) => {
    if (!doc) return;
    const url = doc.fileUrl || doc.url;
    if (url) window.open(url, '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white w-full sm:max-w-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92dvh] rounded-t-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100 gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.cls}`}>
                {statusCfg.label}
              </span>
            </div>
            <h2 className="text-lg font-bold text-gray-900 leading-snug break-words">{task.title}</h2>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-[#37559e] hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit"
            >
              <Edit2 size={17} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Description */}
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Description</h3>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {task.description || <span className="text-gray-400 italic">No description</span>}
            </p>
          </section>

          {/* Dates */}
          {(task.startDate || task.dueDate) && (
            <section>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Dates</h3>
              <div className="flex flex-wrap gap-4">
                {task.startDate && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Calendar size={14} className="text-gray-500" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Start</p>
                      <p className="font-medium">{new Date(task.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                  </div>
                )}
                {task.dueDate && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      new Date(task.dueDate) < new Date() ? 'bg-red-100' : 'bg-gray-100'
                    }`}>
                      <Calendar size={14} className={new Date(task.dueDate) < new Date() ? 'text-red-500' : 'text-gray-500'} />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Due</p>
                      <p className={`font-medium ${new Date(task.dueDate) < new Date() ? 'text-red-600' : ''}`}>
                        {new Date(task.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Assignees */}
          {assignees.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                <span className="flex items-center gap-1.5"><Users size={12} /> Assignees</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {assignees.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                      style={{ backgroundColor: getAvatarColor(a.name) }}
                    >
                      {getInitials(a.name)}
                    </div>
                    <span className="text-sm font-medium text-gray-700">{a.name}</span>
                    {a.role && <span className="text-xs text-gray-400">{a.role}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Evidence */}
          {task.evidence?.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                <span className="flex items-center gap-1.5"><Tag size={12} /> Evidence</span>
              </h3>
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-600" />
                  Loading...
                </div>
              ) : (
                <div className="space-y-1.5">
                  {task.evidence.map((eid, i) => {
                    const doc = documents.find(d => d.id === eid);
                    return (
                      <button
                        key={i}
                        onClick={() => doc && handleDocumentClick(doc)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 bg-gray-50 hover:bg-blue-50 rounded-lg border border-gray-200 hover:border-blue-200 text-left transition-colors group"
                      >
                        {doc?.type === 'link'
                          ? <ExternalLink size={14} className="text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                          : <FileText size={14} className="text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                        }
                        <span className="text-sm text-gray-700 group-hover:text-blue-700 truncate">
                          {doc ? doc.name : `Document ${i + 1}`}
                        </span>
                        {doc && <ExternalLink size={11} className="ml-auto text-gray-300 group-hover:text-blue-400 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsModal;
